import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, ServerError } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { Chip, IconButton, Typography, darken } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import * as Sentry from '@sentry/browser';
import { OperationDefinitionNode } from 'graphql';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useContext, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { appVersion } from '../../../appVersion';
import { useActiveTabContext } from '../../activeTabContext/useActiveTabContext';
import { AuthenticationContext } from '../../security/authentication/AuthenticationContext';
import { getApiOrigin } from '../getApiOirigin';

const cache = new InMemoryCache({
  typePolicies: {
    Workflow_Features_Projects_Query: {
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
    Workflow_Features_Users_Query: {
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
    Workflow_Features_AdwordsApiCalls_Query: {
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
    Workflow_Features_AdWordsSelectors_Query: {
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
  },
});

export function useGraphqlClient() {
  // Performance monitoring
  const performanceMonitorMiddleware = new ApolloLink((operation, forward) => {
    // Setup context for sampling (e.g. we want some extra information to customize performance monitoring sample rate)
    // ... See configureSentry.ts for how it's used
    const prevContext = operation.getContext();
    const samplingContext = {
      polling: prevContext?.polling,
      traceSampleRate: prevContext?.sampleRate,
      mutation: operation.query.definitions.some(
        (definition) => (definition as OperationDefinitionNode)?.operation === 'mutation'
      ),
    };

    // Setup sentry transaction
    const transaction = Sentry.startTransaction(
      { op: 'GraphQL', name: operation.operationName },
      samplingContext
    );
    transaction.setTag('type', samplingContext.mutation ? 'mutation' : 'query');
    transaction.setTag('polling', samplingContext.polling ? 'yes' : 'no');
    const parameters = operation.variables;
    const span = transaction.startChild({ op: 'GraphQL', data: parameters });

    // Set trace header
    const nextContext = { ...prevContext };
    nextContext.headers = { ...nextContext.headers };
    nextContext.headers['sentry-trace'] = span.toTraceparent();
    operation.setContext(nextContext);

    // Do operation
    return forward(operation).map((response) => {
      // Finish sentry transaction
      span.finish();
      transaction.finish();
      return response;
    });
  });

  // Authentication
  const authContext = useContext(AuthenticationContext);
  const activeTabContext = useActiveTabContext();
  const authMiddleware = setContext((request, previousContext) => {
    // Block data retrieval if there's a mismatch
    return authContext.getIdToken().then((idToken) => ({
      headers: {
        authorization: `Bearer ${idToken}`,
        // app-version is used by the backend to figure out if the version is a match between backend/frontend
        'app-version': appVersion,
        'team-id': activeTabContext.currentTeamId,
        ...previousContext?.headers,
      },
    }));
  });

  // Version mismatch middleware
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const versionMismatchLink = new ApolloLink((operation, forward) =>
    forward(operation).map((response) => {
      const context = operation.getContext();
      // We've placed this header on the backend, in the GraphQL controller
      const versionMismatch = context?.response?.headers?.get('versionmatch') == 'false';
      if (versionMismatch) {
        enqueueSnackbar(
          <p>
            <Typography component="span" variant="subtitle2" display="block" gutterBottom>
              Force refresh after ~1 minute
            </Typography>
            <Typography component="span" variant="body2" display="block">
              <ul style={{ padding: 0, margin: 0, paddingLeft: 20, paddingRight: 10 }}>
                <li style={{ paddingBottom: 2 }}>
                  The app has updated, you risk losing new changes
                </li>
                <li style={{ paddingBottom: 2 }}>
                  Wait ~1 minute for everything to be fully deployed, then force refresh
                </li>
                <li>
                  E.g.{' '}
                  <Chip
                    style={{ backgroundColor: darken('rgb(33, 150, 243)', 0.2), color: 'white' }}
                    size="small"
                    label="CMD+SHIFT+R"
                  />{' '}
                  in MacOS Chrome, or{' '}
                  <Chip
                    size="small"
                    label="SHIFT+Click Refresh"
                    style={{ backgroundColor: darken('rgb(33, 150, 243)', 0.2), color: 'white' }}
                  />
                </li>
              </ul>
            </Typography>
          </p>,
          {
            variant: 'info',
            persist: true,
            key: 'old-version',
          }
        );
      }
      return response;
    })
  );

  // Error tools
  const history = useHistory();
  const invitationMatch = useRouteMatch('/t/:token');

  const errorLink = onError((errorResponse) => {
    // Sign out if it's an auth error
    const networkError = errorResponse?.networkError as ServerError;
    if (networkError?.statusCode === 401) {
      authContext.signOut();
      history.push(window.location);
      return;
    }

    // Redirect to register
    if (networkError?.statusCode === 409) {
      if (!!(invitationMatch?.params as any)?.token) {
        // Special redirect if there's an invite token
        history.push(`/register/${(invitationMatch.params as any)?.token}`);
      } else {
        history.push('/register');
      }
      return;
    }

    // Show bad access
    if (networkError?.statusCode === 403) {
      enqueueSnackbar(`Insufficient permissions - action could not be performed`, {
        variant: 'error',
        persist: false,
        preventDuplicate: true,
        key: 'insufficient-permissions',
        action: (
          <IconButton
            onClick={() => closeSnackbar('insufficient-permissions')}
            color={'inherit'}
            size={'small'}
          >
            <Close fontSize={'small'} />
          </IconButton>
        ),
      });
      return;
    }

    if (networkError?.statusCode === 408) {
      enqueueSnackbar(`Timed out - data retrieval took too long`, {
        variant: 'error',
        persist: true,
        preventDuplicate: true,
        key: 'get-data-timed-out',
        action: (
          <IconButton
            onClick={() => closeSnackbar('get-data-timed-out')}
            color={'inherit'}
            size={'small'}
          >
            <Close fontSize={'small'} />
          </IconButton>
        ),
      });
      return;
    }

    // Not a network error and not local => Capture in sentry
    if (
      !errorResponse.networkError &&
      !(
        location.hostname === 'localhost' ||
        location.hostname === '127.0.0.1' ||
        location.hostname === ''
      )
    ) {
      Sentry.withScope((scope) => {
        scope.setExtras({ errorResponse });
        scope.setTag('frontend_version_in_client', appVersion);
        const eventId = Sentry.captureMessage('GraphQL frontend error');
        this.setState({ eventId });
      });
    }

    // Show unique persisted message if mutation error
    const mutationError = errorResponse.operation.query.definitions.some(
      (definition) => (definition as OperationDefinitionNode)?.operation === 'mutation'
    );

    if (mutationError) {
      const errorKey = Date.now().toString();
      if (!errorResponse.operation.operationName?.toLowerCase().includes('log')) {
        enqueueSnackbar(`Error saving your changes`, {
          variant: 'error',
          persist: true,
          key: errorKey,
          action: (
            <IconButton onClick={() => closeSnackbar(errorKey)} color={'inherit'} size={'small'}>
              <Close fontSize={'small'} />
            </IconButton>
          ),
        });
      }
      return;
    }

    // Show temporary error otherwise
    enqueueSnackbar(`Error getting data`, {
      variant: 'error',
      persist: true,
      preventDuplicate: true,
      key: 'get-data',
      action: (
        <IconButton onClick={() => closeSnackbar('get-data')} color={'inherit'} size={'small'}>
          <Close fontSize={'small'} />
        </IconButton>
      ),
    });
  });

  return useMemo(() => {
    return new ApolloClient({
      cache: cache,

      // Compose middleware chain
      link: ApolloLink.from([
        performanceMonitorMiddleware,
        authMiddleware,
        versionMismatchLink,
        errorLink,
        new HttpLink({
          uri: getApiOrigin() + '/api/graphql',
          credentials: 'same-origin',
        }),
      ]),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          errorPolicy: 'all',
        },
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        },
        mutate: {
          errorPolicy: 'all',
        },
      },
    });
  }, [authContext.getIdToken, activeTabContext.currentTeamId]);
}
